import { useState, useRef, useEffect, useMemo } from "react";
import { Head } from "@inertiajs/react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface FAQItem {
  question: string;
  answer: React.ReactNode;
  answerText?: string;
}

interface FAQProps {
  items: readonly FAQItem[];
  title: string;
  highlightWord?: string;
  headingClassName?: string;
  initialVisibleCount?: number;
  sectionId?: string;
  className?: string;
}

function getPlainText(item: FAQItem): string | null {
  if (item.answerText) return item.answerText;
  if (typeof item.answer === "string") return item.answer;
  return null;
}

function buildFAQPageJsonLd(items: readonly FAQItem[]): string {
  const mainEntity = items
    .map((item) => {
      const text = getPlainText(item);
      if (!text) return null;
      return {
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text,
        },
      };
    })
    .filter(Boolean);

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  });
}

function AnswerPanel({ answer, isOpen }: { answer: React.ReactNode; isOpen: boolean }): React.ReactElement {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!isOpen || !contentRef.current) {
      setHeight(0);
      return;
    }

    const el = contentRef.current;
    setHeight(el.scrollHeight);

    const observer = new ResizeObserver(() => {
      setHeight(el.scrollHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <div
      className="overflow-hidden transition-[height] duration-200 ease-in-out"
      style={{ height }}
      aria-hidden={!isOpen}
      inert={!isOpen ? true : undefined}
    >
      <div ref={contentRef} className="px-6 pb-6 pt-2 text-surface-foreground/80 leading-relaxed bg-primary/5">
        {answer}
      </div>
    </div>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function buildSlugs(items: readonly FAQItem[]): string[] {
  const seen = new Map<string, number>();
  return items.map(({ question }) => {
    const base = slugify(question);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}-${count}`;
  });
}

const COLLAPSED_STYLE: React.CSSProperties = { maxHeight: 0, borderTopWidth: 0 };

export function FAQ({
  items,
  title,
  highlightWord,
  headingClassName = "text-4xl font-bold text-center mb-12",
  initialVisibleCount = 3,
  sectionId,
  className,
}: FAQProps): React.ReactElement {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  const hasMore = items.length > initialVisibleCount;
  const slugs = useMemo(() => buildSlugs(items), [items]);

  const titleNode = highlightWord
    ? renderHighlightedTitle(title, highlightWord)
    : title;

  const jsonLd = useMemo(() => buildFAQPageJsonLd(items), [items]);

  return (
    <section id={sectionId} className={className}>
      <Head>
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <div className="max-w-3xl mx-auto">
        <h2 className={headingClassName}>{titleNode}</h2>
        <div className="relative">
          <div className="divide-y divide-border rounded-3xl border bg-background/50 overflow-hidden">
            {items.map(({ question, answer }, index) => {
              const isOpen = openIndex === index;
              const isCollapsed = hasMore && !expanded && index >= initialVisibleCount;
              return (
                <div
                  key={question}
                  id={slugs[index]}
                  style={isCollapsed ? COLLAPSED_STYLE : undefined}
                  aria-hidden={isCollapsed || undefined}
                >
                  <button
                    onClick={() =>
                      setOpenIndex(isOpen ? null : index)
                    }
                    className={`w-full px-6 py-5 flex items-center justify-between text-left transition-colors ${
                      isOpen ? "bg-primary/5" : "hover:bg-primary/5"
                    }`}
                    aria-expanded={isOpen}
                  >
                    <span className="font-semibold pr-4">{question}</span>
                    <ChevronDown
                      className={`size-5 text-surface-foreground/60 flex-shrink-0 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <AnswerPanel answer={answer} isOpen={isOpen} />
                </div>
              );
            })}
          </div>
          {hasMore && !expanded && (
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent rounded-b-3xl pointer-events-none" />
          )}
        </div>
        {hasMore && !expanded && (
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => setExpanded(true)}
            >
              Show all {items.length} questions
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function renderHighlightedTitle(title: string, word: string): React.ReactNode {
  const index = title.indexOf(word);
  if (index === -1) return title;
  return (
    <>
      {title.slice(0, index)}
      <span className="text-primary">{word}</span>
      {title.slice(index + word.length)}
    </>
  );
}
